import React, { useState, useEffect, useCallback } from 'react';
import { IoClose, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import './_imageViewer.scss';

function ImageViewer({ 
  images = [], 
  currentIndex = 0, 
  isOpen = false, 
  onClose = () => {},
  title = 'Image Viewer'
}) {
  const [activeIndex, setActiveIndex] = useState(currentIndex);

  useEffect(() => {
    setActiveIndex(currentIndex);
  }, [currentIndex]);

  const handlePrevious = useCallback(() => {
    setActiveIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
  }, [images.length]);

  const handleNext = useCallback(() => {
    setActiveIndex(prev => prev < images.length - 1 ? prev + 1 : 0);
  }, [images.length]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIndex, images.length, handlePrevious, handleNext, onClose]);

  const handleImageClick = (e) => {
    e.stopPropagation();
    // Click on left side goes to previous, right side goes to next
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const imageWidth = rect.width;
    
    if (clickX < imageWidth / 2) {
      handlePrevious();
    } else {
      handleNext();
    }
  };

  if (!isOpen || images.length === 0) {
    return null;
  }

  const currentImage = images[activeIndex];

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="image-viewer-header">
          <div className="image-viewer-title">
            {title} ({activeIndex + 1} of {images.length})
          </div>
          <div className="image-viewer-controls">
            <button
              type="button"
              className="image-viewer-btn close-btn"
              onClick={onClose}
              title="Close (Esc)"
            >
              <IoClose size={20} />
            </button>
          </div>
        </div>

        {/* Main Image Area */}
        <div className="image-viewer-content">
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                className="image-viewer-nav nav-left"
                onClick={handlePrevious}
                title="Previous image (←)"
              >
                <IoChevronBack size={24} />
              </button>
              <button
                type="button"
                className="image-viewer-nav nav-right"
                onClick={handleNext}
                title="Next image (→)"
              >
                <IoChevronForward size={24} />
              </button>
            </>
          )}

          {/* Image */}
          <div className="image-viewer-image-container">
            <img
              src={currentImage.image}
              alt={`${title} ${activeIndex + 1}`}
              className="image-viewer-image"
              onClick={handleImageClick}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="image-viewer-error" style={{ display: 'none' }}>
              <div className="error-content">
                <p>Failed to load image</p>
                <small>Please check the image URL or try again later</small>
              </div>
            </div>
          </div>
        </div>

        {/* Thumbnail Navigation */}
        {images.length > 1 && (
          <div className="image-viewer-thumbnails">
            {images.map((img, index) => (
              <button
                key={img.id || index}
                type="button"
                className={`image-viewer-thumb ${index === activeIndex ? 'active' : ''}`}
                onClick={() => setActiveIndex(index)}
              >
                <img
                  src={img.image}
                  alt={`Thumbnail ${index + 1}`}
                  className="thumb-image"
                />
                {img.is_primary && (
                  <div className="thumb-badge">Primary</div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Footer Info */}
        <div className="image-viewer-footer">
          <div className="image-info">
            {currentImage.is_primary && (
              <span className="primary-badge">Primary Image</span>
            )}
          </div>
          <div className="navigation-hint">
            {images.length > 1 && (
              <span>Use ← → arrow keys or click on image sides to navigate</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageViewer;
